import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PaystackPop from '@paystack/inline-js';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import baseUrl from '../../../../BaseURL';
import { ContextData } from '../../../../context/Context';
import './WalletRecharge.css';

export function calculateRecharge(quantity, fee) {
  const count = Number(quantity);
  const price = Number(fee);
  const totalKobo = Math.round(price * 100) * count;
  return Number.isSafeInteger(count) && count >= 1 && count <= 100000 && Number.isFinite(price) && price > 0 && Number.isSafeInteger(totalKobo) && totalKobo <= 2147483647 ? totalKobo / 100 : null;
}
const money = value => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(value || 0);
const errorMessage = err => err.response?.data?.message || 'Unable to complete this request. Please try again.';

export default function WalletRechargeDialog({ balance }) {
  const navigate = useNavigate();
  const { Person, accesstoken, setRefresh, setRefreshAmount } = useContext(ContextData);
  const [open, setOpen] = useState(false);
  const [fees, setFees] = useState([]);
  const [pending, setPending] = useState([]);
  const [configured, setConfigured] = useState(false);
  const [feeId, setFeeId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(null);
  const [checkoutReference, setCheckoutReference] = useState('');
  const [validatingReference, setValidatingReference] = useState('');
  const inFlight = useRef(false);
  const dialogId = React.useId();
  const selectedFee = fees.find(fee => String(fee.SetPayment_id) === String(feeId));
  const total = calculateRecharge(quantity, selectedFee?.fee);
  const config = useCallback(() => ({ withCredentials: true, headers: { authorization: `Bearer ${accesstoken}` } }), [accesstoken]);

  const loadOptions = useCallback(async () => {
    setLoading(true);
    setMessage('');
    setConfigured(false);
    const [feeResult, walletResult] = await Promise.allSettled([
      axios.get(`${baseUrl}/auth/get_set_payment`, config()),
      axios.get(`${baseUrl}/auth/wallet/recharges/options`, { ...config(), timeout: 10000 })
    ]);
    const messages = [];
    if (feeResult.status === 'fulfilled' && Array.isArray(feeResult.value.data)) {
      const availableFees = feeResult.value.data.filter(fee =>
        fee.SetPayment_id != null && !String(fee.registration_type || '').toLowerCase().includes('update') && Number.isFinite(Number(fee.fee)) && Number(fee.fee) > 0
      );
      setFees(availableFees);
      setFeeId(previous => availableFees.some(fee => String(fee.SetPayment_id) === previous) ? previous : '');
    } else {
      setFees([]);
      setFeeId('');
      messages.push('Registration types could not be loaded. Please retry.');
    }
    if (walletResult.status === 'fulfilled' && walletResult.value.data?.status === true && Array.isArray(walletResult.value.data.pending) && typeof walletResult.value.data.configured === 'boolean') {
      setPending(walletResult.value.data.pending);
      setConfigured(walletResult.value.data.configured);
    } else {
      setPending([]);
      messages.push(walletResult.status === 'rejected' && walletResult.reason.response?.data?.message
        ? walletResult.reason.response.data.message
        : 'Online wallet recharge is unavailable on the current API server. The updated backend must be running before payment can start.');
    }
    setMessage(messages.join(' '));
    setLoading(false);
  }, [config]);

  useEffect(() => { if (open) loadOptions(); }, [open, loadOptions]);

  const verifyPayment = useCallback(async reference => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setMessage('');
    try {
      const { data } = await axios.put(`${baseUrl}/auth/user_amount`, { reference }, config());
      if (data.pending) {
        setValidatingReference(reference);
        setCheckoutReference('');
        window.dispatchEvent(new CustomEvent('fin-wallet-validation', { detail: { reference } }));
        return;
      }
      setSuccess(data);
      setValidatingReference('');
      setCheckoutReference('');
      setPending(previous => previous.filter(item => item.reference !== reference));
      setRefresh(previous => !previous);
      setRefreshAmount(previous => !previous);
    } catch (err) { setMessage(`${errorMessage(err)} Reference: ${reference}`); }
    finally { inFlight.current = false; setBusy(false); }
  }, [config, setRefresh, setRefreshAmount]);

  useEffect(() => {
    function onCredited(event) {
      if (event.detail?.reference === validatingReference) {
        setSuccess(event.detail);
        setValidatingReference('');
        setPending(previous => previous.filter(item => item.reference !== event.detail.reference));
      }
    }
    window.addEventListener('fin-wallet-credited', onCredited);
    return () => window.removeEventListener('fin-wallet-credited', onCredited);
  }, [validatingReference]);

  const pay = async event => {
    event.preventDefault();
    if (total === null || busy || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setMessage('');
    setSuccess(null);
    try {
      const { data } = await axios.post(`${baseUrl}/auth/wallet/recharges/initialize`, { fee_id: Number(feeId), quantity: Number(quantity) }, config());
      if (typeof data?.reference !== 'string' || !data.reference.startsWith('FIN-WALLET-') || typeof data.access_code !== 'string' || !data.access_code.trim() || !Number.isSafeInteger(data.amount) || data.amount <= 0 || data.quantity !== Number(quantity)) {
        throw new Error('The payment server did not return valid checkout details. Please contact an administrator to check wallet recharge setup.');
      }
      setCheckoutReference(data.reference);
      setPending(previous => [{ reference: data.reference, quantity: data.quantity, amount_kobo: data.amount }, ...previous]);
      inFlight.current = false;
      // Resume the server-created transaction, preserving its trusted amount and reference.
      const popup = new PaystackPop();
      popup.resumeTransaction(data.access_code, {
        onSuccess: () => { setBusy(false); setOpen(false); navigate(`/dashboard/payment-response/${encodeURIComponent(data.reference)}`); },
        onCancel: () => { setBusy(false); setMessage('Checkout closed. If you paid, use Check payment below to update your wallet.'); },
        onError: () => { setBusy(false); setMessage('Checkout could not load. Retry or check the payment reference below if you already paid.'); }
      });
    } catch (err) {
      inFlight.current = false;
      setBusy(false);
      setMessage(err.response ? errorMessage(err) : err.message || errorMessage(err));
    }
  };

  function close() { if (!busy) setOpen(false); }

  return <>
    <Button variant="outlined" onClick={() => { setOpen(true); setSuccess(null); setMessage(''); }} startIcon={<AccountBalanceWalletOutlinedIcon />}>Recharge wallet</Button>
    <Dialog open={open} onClose={close} disableEnforceFocus={busy && Boolean(checkoutReference)} fullWidth maxWidth="sm" className="fin-wallet-dialog" aria-labelledby={`${dialogId}-title`}>
      <DialogTitle component="div" className="fin-wallet-heading"><div><p>REGISTRATION WALLET</p><h2 id={`${dialogId}-title`}>Recharge your wallet</h2></div><IconButton onClick={close} disabled={busy} aria-label="Close wallet recharge"><CloseIcon /></IconButton></DialogTitle>
      <DialogContent className="fin-wallet-content">
        <div className="fin-wallet-account"><AccountBalanceWalletOutlinedIcon /><div><strong>{Person?.fname} {Person?.lname}</strong><span>{Number(balance || 0).toLocaleString()} registration credits available</span></div></div>
        {validatingReference && !success && <Alert severity="info" role="status">Validating your payment. Your account will be credited once confirmed. You can close this dialog.</Alert>}
        {message && <Alert severity="warning" role="alert" action={!busy && <Button color="inherit" size="small" onClick={loadOptions}>Retry</Button>}>{message}</Alert>}
        {success ? <div className="fin-wallet-success" role="status"><h3>{success.alreadyCredited ? 'Payment already credited' : 'Wallet recharged'}</h3><p>{Number(success.quantity).toLocaleString()} registration credits {success.alreadyCredited ? 'were already added to' : 'have been added to'} your wallet.</p><small>Reference: {success.reference}</small><Button onClick={() => { setSuccess(null); setQuantity('1'); loadOptions(); }}>Make another recharge</Button></div> : <form id={`${dialogId}-form`} onSubmit={pay}>
          <p className="fin-wallet-intro">Choose a registration type and the number of credits to buy. Your total is calculated automatically.</p>
          {loading ? <div className="fin-wallet-loading" role="status"><CircularProgress size={22} /> Loading registration fees…</div> : <>
            {!configured && <Alert severity="info">Checkout availability could not be confirmed. Select your credits and press Pay to check again.</Alert>}
            {fees.length === 0 && <p className="fin-wallet-intro">No registration fees are available. Contact an administrator.</p>}
            <TextField select fullWidth label="Registration type" value={feeId} onChange={event => setFeeId(event.target.value)} margin="normal" disabled={busy || !fees.length} required>{fees.map(fee => <MenuItem key={fee.SetPayment_id} value={String(fee.SetPayment_id)}>{fee.registration_type} — {money(fee.fee)} per credit</MenuItem>)}</TextField>
            <TextField fullWidth label="Number of registration credits" type="number" value={quantity} onChange={event => setQuantity(event.target.value)} margin="normal" disabled={busy} inputProps={{ min: 1, max: 100000, step: 1 }} error={quantity !== '' && selectedFee && total === null} helperText="Enter a whole number. One credit covers one member registration." required />
            <div className="fin-wallet-summary"><h3>Recharge summary</h3><dl><div><dt>Price per credit</dt><dd>{money(selectedFee?.fee)}</dd></div><div><dt>Registration credits</dt><dd>{Number(quantity || 0).toLocaleString()}</dd></div><div className="fin-wallet-total"><dt>Total to pay</dt><dd>{money(total)}</dd></div></dl><p>Payment is processed by Paystack. Credits are added after payment confirmation.</p></div>
          </>}
        </form>}
        {!success && pending.length > 0 && <section className="fin-wallet-pending" aria-label="Pending wallet payments"><h3>Check a previous payment</h3><p>Already paid? Check its status to receive your credits without paying again.</p>{pending.map(item => <div className="fin-wallet-pending-row" key={item.reference}><div><strong>{item.quantity} credits · {money(item.amount_kobo / 100)}</strong><small>{item.reference}</small></div><Button variant="text" disabled={busy} onClick={() => verifyPayment(item.reference)}>Check payment</Button></div>)}</section>}
        {busy && <p className="fin-wallet-loading" role="status"><CircularProgress size={18} />{checkoutReference ? 'Complete checkout or wait for payment confirmation…' : 'Connecting to Paystack…'}</p>}
      </DialogContent>
      <DialogActions className="fin-wallet-actions"><Button onClick={close} disabled={busy}>{success ? 'Done' : 'Cancel'}</Button>{!success && <Button variant="contained" type="submit" form={`${dialogId}-form`} disabled={busy || loading || total === null}>Pay {money(total)}</Button>}</DialogActions>
    </Dialog>
  </>;
}
